import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Checkbox, Collapse, IconButton, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import PlanFilters from 'components/plan/PlanFilters';
import PlanAccordion from 'components/plan/PlanAccordion';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import routes from 'common/routes';
import { canModify } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import planSlice from 'store/modules/plan';

const PLATFORM_ORDER = ['otrix_app', 'active_plus', 'otrix_play', 'otrix_gym'];

function PlanList() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['buttons', 'errors', 'labels', 'routes', 'tables', 'options']);
  const pageTitle = t('plan.list', { ns: 'routes' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const filters = useSelector((state) => state.plan.filters);
  const pagination = useSelector((state) => state.plan.pagination);
  const plans = useSelector((state) => state.plan.plans);
  const refresh = useSelector((state) => state.plan.refresh);
  const role = useSelector((state) => state.auth.user.role);
  const status = useSelector((state) => state.plan.status);

  const canModifyData = canModify(role);

  const platformLabels = {
    otrix_app: t('platform.otrixApp', { ns: 'options', defaultValue: 'Otrix Personalizado App' }),
    active_plus: t('platform.activePlus', { ns: 'options', defaultValue: 'Otrix Active Plus' }),
    otrix_play: t('platform.otrixPlay', { ns: 'options', defaultValue: 'OTRIX Play' }),
    otrix_gym: t('platform.otrixGym', { ns: 'options', defaultValue: 'OTRIX Gym' })
  };

  const [openPlatforms, setOpenPlatforms] = useState(
    PLATFORM_ORDER.reduce((acc, p) => ({ ...acc, [p]: true }), {})
  );
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const listPlans = useCallback(() => {
    dispatch(planSlice.actions.list({ pagination, filters }));
  }, [dispatch, filters, pagination]);

  useEffect(() => {
    listPlans();
  }, [listPlans, refresh]);

  useEffect(() => {
    setAppBarButton(
      canModifyData ? (
        <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createPlan.path} />
      ) : null
    );
    setAppBarTitle(pageTitle);
  }, [canModifyData, pageTitle, setAppBarButton, setAppBarTitle, t]);

  const toggle = (p) => setOpenPlatforms((prev) => ({ ...prev, [p]: !prev[p] }));

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleDeleteConfirm = () => {
    const plansWithClients = plans.filter((plan) => selected.includes(plan.id) && plan.clients > 0);
    if (plansWithClients.length > 0) {
      const planNames = plansWithClients.map((p) => p.name).join(', ');
      enqueueSnackbar(t('plan.cannotDeleteWithClients', { ns: 'errors', plans: planNames }), { variant: 'error' });
      setConfirmOpen(false);
      return;
    }
    dispatch(planSlice.actions.remove(selected));
    setSelected([]);
    setConfirmOpen(false);
  };

  const plansByPlatform = PLATFORM_ORDER.reduce((acc, p) => {
    const filtered = plans.filter((plan) => (plan.platform || 'otrix_app') === p);
    if (filtered.length > 0) acc[p] = filtered;
    return acc;
  }, {});

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <PlanFilters />

      {canModifyData && selected.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)} size="small">
            {t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })} ({selected.length})
          </Button>
        </Box>
      )}

      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' }) }}
        isValid={plans.length > 0}
        skeletonProps={{ rows: 5 }}
        status={status}
        type="table">
        <Stack spacing={2.5}>
          {Object.entries(plansByPlatform).map(([p, sectionPlans]) => {
            const isOpen = openPlatforms[p];
            return (
              <Box key={p}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => toggle(p)}
                  sx={{
                    px: 0.5,
                    py: 1,
                    mb: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'hairline.main'
                  }}>
                  <Stack direction="row" spacing={1.25} alignItems="baseline">
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                      {platformLabels[p]}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                      {sectionPlans.length}{' '}
                      {sectionPlans.length === 1
                        ? t('plan.singular', { ns: 'tables', defaultValue: 'plan' })
                        : t('plan.plural', { ns: 'tables', defaultValue: 'planes' })}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label={isOpen ? 'contraer' : 'expandir'}
                    sx={{ color: isOpen ? 'gold.main' : 'text.secondary', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)' }}>
                    <ExpandMoreIcon />
                  </IconButton>
                </Stack>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Stack spacing={1.25}>
                    {sectionPlans.map((plan) => (
                      <PlanAccordion
                        key={plan.id}
                        plan={plan}
                        leading={
                          canModifyData ? (
                            <Checkbox
                              size="small"
                              checked={selected.includes(plan.id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelect(plan.id)}
                              sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'gold.main' } }}
                            />
                          ) : null
                        }
                        actions={
                          canModifyData ? (
                            <Button
                              size="small"
                              component={RouterLink}
                              to={routes.editPlan.path.replace(':id', plan.id)}
                              startIcon={<EditIcon fontSize="small" />}
                              sx={{
                                fontWeight: 700,
                                textTransform: 'none',
                                color: 'gold.main',
                                '&:hover': { backgroundColor: 'gold.soft' }
                              }}>
                              {t('edit', { ns: 'buttons', defaultValue: 'Editar' })}
                            </Button>
                          ) : null
                        }
                      />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </LoadingBlock>

      <ConfirmDeleteModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteConfirm} count={selected.length} />
    </>
  );
}

export default PlanList;
